import { Component, OnInit } from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { EducationService } from "../../services/education/education.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ERTEducation } from "../../interfaces/ert-education/ert-education";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { PicklistService } from "../../services/administration/picklist/picklist.service";
import { IPicklistValuesData } from "../../interfaces/administration/picklistValuesData";
import { PicklistItem } from "../../shared/picklist/picklist-select.component";
import { FireDepartmentsBcTurnoversService } from "../../services/firedepartments/fireDepartmentsBcTurnovers.service";
import { forkJoin } from "rxjs";
import { LoggedUserService } from "../../services/common/logged-user.service";
import { ILoggedUserData } from "../../interfaces/common/loggedUserData";

@Component({
  selector: "app-ert-education-view",
  templateUrl: "./ert-education-view.component.html",
})
export class ErtEducationViewComponent implements OnInit {
  title = "ERT Education";
  userNumber: number;
  currentEducation: ERTEducation;
  currentLoggedInUser: ILoggedUserData;
  dataSource = new MatTableDataSource<ERTEducation>();
  selectedElement: any;
  isEducationLoading = false;
  isUserUpdating = false;
  validationMessages: string[] = [];
  educationForm: FormGroup;

  schoolAttendedId: number = 0;
  schoolDivisionId: number = 0;

  selectedSchoolAttended: IPicklistValuesData;
  selectedSchoolDivision: IPicklistValuesData;

  isSchoolQualified: boolean = false;

  schoolInfo: any = {
    userErtName: "",
    facilityName: "",
    schoolAttended: "",
    hours: 0,
    schoolDivision: "",
    employeeLink: "",
    attendanceDate: "",
    onOffSite: "",
    comments: "",
    isQualifiedForErtRoster: false,
    registrationNumber: "",
    recertDate: "",
  };

  selectedSchoolAttendedItem!: PicklistItem;
  selectedSchoolDivisionItem!: PicklistItem;

  constructor(
    private educationService: EducationService,
    private pickListsService: PicklistService,
    private bcTurnOverService: FireDepartmentsBcTurnoversService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private loggedUser: LoggedUserService
  ) {
    this.educationForm = this.fb.group({
      schoolAttended: ["", Validators.required],
      schoolDivision: ["", Validators.required],
      facilityName: ["", Validators.required],
      hours: [""],
      attendanceDate: [""],
      onOffSite: [""],
      comments: [""],
      registrationNumber: [""],
      recertDate: [""],
      isQualifiedForErtRoster: [""],
    });
  }

  ngOnInit() {
    this.isEducationLoading = true;
    forkJoin({
      schoolAttended: this.pickListsService.getFieldIdByName("school attended"),
      schoolDivision: this.pickListsService.getFieldIdByName("school division"),
    }).subscribe((results) => {
      this.schoolAttendedId = results.schoolAttended || 0;
      this.schoolDivisionId = results.schoolDivision || 0;

      this.route.queryParams.subscribe((params) => {
        const ertEducationId = params["id"];
        this.selectedElement = this.dataSource.data.find(
          (element) => element.id === ertEducationId
        );
        this.userNumber = ertEducationId;
        this.loggedUser.getLoggedUserData().subscribe((user) => {
          this.currentLoggedInUser = user;
          this.getEducationDetails();
        });
      });
    });
  }

  getEducationDetails() {
    this.isEducationLoading = true;
    this.educationService.getEducationById(this.userNumber, Number(this.currentLoggedInUser.facilityId) || 0).subscribe({
      next: (education: ERTEducation) => {
        if(education.facilityId !== Number(this.currentLoggedInUser.facilityId)) {
          this.isEducationLoading = false;
          this.router.navigate(["/no-access"]);
          return;
        }
        this.currentEducation = education;
        const selectedSchoolAttended = this.getSelectedPicklistField(
          this.schoolAttendedId
        );
        const selectedSchoolDivision = this.getSelectedPicklistField(
          this.schoolDivisionId
        );

        this.schoolInfo = {
          ...education,
          facilityName: education.facility.name,
          onOffSite: education.onOffSite === 1 ? "On Site" : "Off Site",
          attendanceDate: this.formatDate(education.attendanceDate),
          recertDate: this.formatDate(education.recertDate),
          userErtName: education.userErt.fullName,
          employeeLink: education.userErtId,
          schoolAttended: selectedSchoolAttended
            ? selectedSchoolAttended.id.toString()
            : "",
          schoolDivision: selectedSchoolDivision
            ? selectedSchoolDivision.id.toString()
            : "",
        };

        this.educationForm.patchValue(this.schoolInfo);

        if (
          selectedSchoolAttended &&
          selectedSchoolAttended.isOnPrem === true
        ) {
          this.isSchoolQualified = true;
          this.educationForm.get("isQualifiedForErtRoster").setValue(true);
          this.educationForm.get("isQualifiedForErtRoster").disable();
        } else {
          this.isSchoolQualified = false;
        }

        this.isEducationLoading = false;
      },
      error: (err) => {
        console.error("Error loading user details:", err);
        this.isEducationLoading = false;
      },
    });
  }

  getSelectedPicklistField(fieldId: number) {
    let selectedPicklistValue: IPicklistValuesData =
      this.currentEducation?.picklistValues.find(
        (x) => x.picklistFieldId === fieldId
      );
    return selectedPicklistValue;
  }

  formatDate(date: any): string {
    if (!date || isNaN(new Date(date).getTime())) {
      return "";
    }
    return new Date(date).toISOString().split("T")[0];
  }

  onSchoolAttendedSelected(schoolAttended: IPicklistValuesData): void {
    this.selectedSchoolAttended = schoolAttended;
    this.isSchoolQualified = schoolAttended && schoolAttended.isOnPrem === true;

    if (this.isSchoolQualified) {
      this.educationForm.get("isQualifiedForErtRoster").setValue(true);
      this.educationForm.get("isQualifiedForErtRoster").disable();
    } else {
      this.educationForm.get("isQualifiedForErtRoster").enable();
    }
  }

  onSchoolDivisionSelected(schoolDivision: IPicklistValuesData): void {
    this.selectedSchoolDivision = schoolDivision;
  }

  updateEducation() {
    this.validationMessages = this.educationService.validateViewForm(
      this.educationForm
    );
    if (this.validationMessages.length > 0) {
      console.error("Validation errors:", this.validationMessages);
      return;
    }
    this.isUserUpdating = true;

    const formValues = {
      ...this.educationForm.value,
      isQualifiedForErtRoster: this.isSchoolQualified
        ? true
        : this.educationForm.get("isQualifiedForErtRoster").value,
    };

    const updatedUser = {
      ...this.currentEducation,
      ...formValues,
      onOffSite: formValues.onOffSite === "On Site" ? 1 : 2,

      recertDate: formValues.recertDate ? formValues.recertDate : null,

      schoolAttendedId: this.selectedSchoolAttended
        ? this.selectedSchoolAttended.id
        : this.educationForm.get("schoolAttended").value,
      schoolDivisionId: this.selectedSchoolDivision
        ? this.selectedSchoolDivision.id
        : this.educationForm.get("schoolDivision").value,
      picklistValues: [
        {
          id: this.selectedSchoolAttended
            ? this.selectedSchoolAttended.id
            : this.educationForm.get("schoolAttended").value,
          value: this.selectedSchoolAttended
            ? this.selectedSchoolAttended.value
            : undefined,
          picklistFieldId: this.schoolAttendedId,
          facilityId: Number(this.currentLoggedInUser?.facilityId) || 1,
        },
        {
          id: this.selectedSchoolDivision
            ? this.selectedSchoolDivision.id
            : this.educationForm.get("schoolDivision").value,
          value: this.selectedSchoolDivision
            ? this.selectedSchoolDivision.value
            : undefined,
          picklistFieldId: this.schoolDivisionId,
          facilityId: Number(this.currentLoggedInUser?.facilityId) || 1,
        },
      ],
    };
    this.educationService.updateEducation(updatedUser, Number(this.currentLoggedInUser?.facilityId) || 0).subscribe({
      next: (_) => {
        this.isUserUpdating = false;
        this.router.navigate(["/ert-education"]);
      },
    });
  }

  getLastPostedShoolAttended(picklistValues: IPicklistValuesData[]): string {
    let name: string = this.bcTurnOverService.getValueForField(
      picklistValues,
      this.schoolAttendedId
    );
    return name;
  }
  getLastPostedShoolDivision(picklistValues: IPicklistValuesData[]): string {
    let name: string = this.bcTurnOverService.getValueForField(
      picklistValues,
      this.schoolDivisionId
    );
    return name;
  }

  showQualifiedList() {
    window.open(
      "/ert-education/qualified-list",
      "_blank",
      "width=800, height=600"
    );
  }

  addEducationForThisUser(employeeName: string, userId: number) {
    this.router.navigate(["/ert-education/new-entry"], {
      queryParams: { name: employeeName, userId: userId },
    });
  }

  deleteEducation() {
    if (
      !window.confirm("Are you sure you want to delete this education record?")
    ) {
      return;
    }
    this.educationService
      .deleteEducation(
        Number(this.userNumber),
        Number(this.currentLoggedInUser?.facilityId) || 0
      )
      .subscribe({
        next: (_) => {
          this.snackBar.open("Education record deleted.", "Close", {
            duration: 3000,
          });
          this.router.navigate(["/ert-education"]);
        },
        error: (err) => {
          console.error("Error deleting education record:", err);
          this.snackBar.open("Error deleting education record.", "Close", {
            duration: 3000,
          });
        },
      });
  }
}
